import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import * as OverViewApi from "../../api/overview";
import {
  getDashboardErrorMessage,
  unwrapDashboardResponse,
} from "../../api/dashboardResponse";
import { addAsyncCases } from "../utils/asyncReducers";

const initialState = {
  data: null,
  businessName: null,
  ui: {
    loading: false,
    msg: "",
    chartLoading: false,
    dashboardLoding: false,
    chartUi: false,
  },
  status: "",
  overallCount: null,
  chartData: [],
  topCategories: [],
  trends: [],
  flaggedConcerns: [],
  error: null,
};

const rejectDashboardRequest = (error, rejectWithValue, fallbackMessage) => {
  const message = getDashboardErrorMessage(error, fallbackMessage);
  toast.error(message);
  return rejectWithValue(message);
};

const getOverviewData = createAsyncThunk(
  "overview/getOverviewData",
  async (params, { rejectWithValue }) => {
    try {
      const res = await OverViewApi.getAllOverviewCount(params);
      return unwrapDashboardResponse(res, "Failed to load dashboard overview");
    } catch (error) {
      return rejectDashboardRequest(
        error,
        rejectWithValue,
        "Failed to load dashboard overview"
      );
    }
  }
);

const getAllDahsbordRightCardStore = createAsyncThunk(
  "get/sentimentReport",
  async (params, { rejectWithValue }) => {
    try {
      const res = await OverViewApi.getDashboardRightCard(params);
      return unwrapDashboardResponse(res, "Failed to load dashboard report");
    } catch (error) {
      return rejectDashboardRequest(
        error,
        rejectWithValue,
        "Failed to load dashboard report"
      );
    }
  }
);

const exportOverviewReport = createAsyncThunk(
  "overview/exportOverviewReport",
  async (params, { rejectWithValue }) => {
    try {
      const res = await OverViewApi.getAllOverviewReport(params);
      return unwrapDashboardResponse(
        res,
        "Failed to export conversation report"
      );
    } catch (error) {
      return rejectDashboardRequest(
        error,
        rejectWithValue,
        "Failed to export conversation report"
      );
    }
  }
);

const getBusinessNameData = createAsyncThunk(
  "overview/getBusinessNameData",
  async (params, { rejectWithValue }) => {
    try {
      const res = await OverViewApi.getBusinessName(params);
      return unwrapDashboardResponse(res, "Failed to load businesses");
    } catch (error) {
      return rejectDashboardRequest(
        error,
        rejectWithValue,
        "Failed to load businesses"
      );
    }
  }
);

const getOverviewChartData = createAsyncThunk(
  "overview/getOverviewChartData",
  async (params, { rejectWithValue }) => {
    try {
      const res = await OverViewApi.getOverviewData(params);
      return unwrapDashboardResponse(res, "Failed to load chart data");
    } catch (error) {
      return rejectDashboardRequest(
        error,
        rejectWithValue,
        "Failed to load chart data"
      );
    }
  }
);

const getBusinessByClient = createAsyncThunk(
  "overview/getBusinessByClient",
  async (params, { rejectWithValue }) => {
    try {
      const res = await OverViewApi.getTheBussinessByClientBased(params);
      return unwrapDashboardResponse(res, "Failed to load client businesses");
    } catch (error) {
      return rejectDashboardRequest(
        error,
        rejectWithValue,
        "Failed to load client businesses"
      );
    }
  }
);

const getBusinessDetails = createAsyncThunk(
  "overview/getBusinessDetails",
  async (params, { rejectWithValue }) => {
    try {
      const res = await OverViewApi.getName(params);
      const body = unwrapDashboardResponse(res, "Failed to load business details");

      const businessName =
        typeof body === "string" ? body : body?.businessName || body?.name || "";

      return {
        businessId: params?.businessId || null,
        businessName,
      };
    } catch (error) {
      return rejectDashboardRequest(
        error,
        rejectWithValue,
        "Failed to load business details"
      );
    }
  }
);

const overviewSlice = createSlice({
  name: "overview",
  initialState,
  reducers: {
    resetState: () => initialState,
    setChartUi: (state) => {
      state.ui.chartUi = false;
    },
    resetOverAllCount: (state) => {
      state.overallCount = null;
    },
    resetChartData: (state) => {
      state.chartData = [];
    },
  },
  extraReducers: (builder) => {
    addAsyncCases(builder, getOverviewData, {
      onPending: (state) => {
        state.ui.chartUi = false;
      },
      onFulfilled: (state, action) => {
        state.overallCount = action.payload;
        state.ui.chartUi = true;
        state.chartData = [];
      },
      onRejected: (state) => {
        state.overallCount = null;
        state.ui.chartUi = false;
        state.chartData = [];
      },
    });
    addAsyncCases(builder, getBusinessNameData);
    addAsyncCases(builder, getBusinessDetails, {
      onFulfilled: (state, action) => {
        state.businessName = action.payload?.businessName || null;
        state.data =
          action.payload?.businessId && action.payload?.businessName
            ? [action.payload]
            : [];
      },
      onRejected: (state) => {
        state.businessName = null;
      },
    });
    addAsyncCases(builder, getOverviewChartData, {
      loadingKey: ["ui", "chartLoading"],
      onFulfilled: (state, action) => {
        state.chartData = action.payload;
      },
      onRejected: (state) => {
        state.chartData = [];
      },
    });
    addAsyncCases(builder, getBusinessByClient);
    addAsyncCases(builder, getAllDahsbordRightCardStore, {
      loadingKey: ["ui", "dashboardLoding"],
      onFulfilled: (state, action) => {
        const data = action.payload || {};
        state.topCategories = data.top3Categories || [];
        state.trends = data.trends || [];
        state.flaggedConcerns = data.flaggedConcerns || [];
      },
      onRejected: (state) => {
        state.topCategories = [];
        state.trends = [];
        state.flaggedConcerns = [];
      },
    });
  },
});

const { resetState, setChartUi, resetOverAllCount, resetChartData } =
  overviewSlice.actions;

export default overviewSlice.reducer;

export {
  getOverviewData,
  getAllDahsbordRightCardStore,
  resetState,
  getBusinessNameData,
  getOverviewChartData,
  setChartUi,
  resetOverAllCount,
  resetChartData,
  getBusinessByClient,
  getBusinessDetails,
  exportOverviewReport,
};
